import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { payoutsApi } from './payouts'
import type { ApprovePayoutsRequest, PayoutStatus } from './payouts'
import { queryKeys } from './query-keys'

/** Full payout ledger for a team (mock-only endpoint). */
export function usePayouts(teamId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.payouts.list(teamId ?? 'none'),
    queryFn: () => payoutsApi.list(Number(teamId)),
    enabled: !!teamId,
    staleTime: 30 * 1000,
  })
}

/**
 * Approve a period's payouts (POST /teams/:teamId/payouts/approve).
 * Each approval also lands a 'payout' event in the recipient's activity
 * feed, so the activity cache is invalidated alongside the ledger.
 */
export function useApprovePayouts(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ApprovePayoutsRequest) =>
      payoutsApi.approve(Number(teamId), data),
    onSuccess: () => {
      if (teamId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.payouts.list(teamId) })
      }
      qc.invalidateQueries({ queryKey: queryKeys.activity.all })
    },
  })
}

/** Transition a payout record's status (PATCH /payouts/:id). */
export function useUpdatePayoutStatus(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ payoutId, status }: { payoutId: number; status: PayoutStatus }) =>
      payoutsApi.updateStatus(payoutId, status),
    onSuccess: () => {
      if (teamId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.payouts.list(teamId) })
      }
    },
  })
}
